/**
 * PATH: apps/qa/src/pages/records/JobQaHomeTable.tsx
 * FILE_ID: QA-RECORDS-JOB-SHEET-001
 * PURPOSE: QA home jobs on the People contacts grid.
 * WHY: A hand-built sheet clipped Date and did not sort or right-click.
 * NON-RESPONSIBILITIES: Does not create the blank QA. The page owns that action.
 * The live QA bundle is path-scoped, so a later production commit that
 * does not touch this app leaves the previous bundle in place.
 */

import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { DirectorySheet, type ContextMenuItem, type DirectoryColumn, type DirectorySort } from '@quotech/ui'
import { qaRecordPath } from '../records-list/openJobQaInHub'
import { formatDate } from '@/lib/utils'
import type { QaJobRow } from './useJobRows'

type InspectionKey = 'job' | 'client' | 'site' | 'inspection' | 'status' | 'records' | 'date'

const COLUMNS: DirectoryColumn<InspectionKey>[] = [
  { key: 'job', label: 'Job', sortable: true, width: 'min-w-[8rem]' },
  { key: 'client', label: 'Client', sortable: true, width: 'min-w-[10rem]' },
  { key: 'site', label: 'Site', sortable: true, width: 'min-w-[10rem]' },
  { key: 'inspection', label: 'Inspection', sortable: true, width: 'min-w-[10rem]' },
  { key: 'status', label: 'Status', sortable: true, width: 'min-w-[8rem]' },
  { key: 'records', label: 'Records', sortable: true, align: 'right', width: 'min-w-[5.5rem]' },
  { key: 'date', label: 'Date', sortable: true, width: 'min-w-[120px]' },
]

function statusText(status: string) {
  return status.replace(/_/g, ' ')
}

function statusTone(status: string) {
  if (status === 'submitted') return 'text-emerald-700 dark:text-emerald-400'
  if (status === 'in_progress') return 'text-blue-700 dark:text-blue-400'
  if (status === 'ready_for_pack') return 'text-amber-700 dark:text-amber-400'
  if (status === 'published') return 'text-purple-700 dark:text-purple-400'
  return 'text-muted-foreground'
}

function sortValue(row: QaJobRow, key: InspectionKey): string | number {
  const latest = row.latest
  if (key === 'job') return String(row.job_id)
  if (key === 'client') return row.client_name || ''
  if (key === 'site') return row.site_address || ''
  if (key === 'inspection') return latest?.inspection_name || ''
  if (key === 'status') return latest?.status || ''
  if (key === 'records') return row.qa_count
  return latest?.created_at || ''
}

function openRow(row: QaJobRow, pendingJobId: string | null, onOpenEmpty: (row: QaJobRow) => void) {
  if (pendingJobId === String(row.job_id)) return
  if (row.latest?.id) window.location.assign(qaRecordPath(row.latest.id))
  else onOpenEmpty(row)
}

export function JobQaHomeTable({
  rows,
  pendingJobId,
  onOpenEmpty,
  onAdd,
}: {
  rows: QaJobRow[]
  pendingJobId: string | null
  onOpenEmpty: (row: QaJobRow) => void
  onAdd?: () => void
}) {
  const [sort, setSort] = useState<DirectorySort<InspectionKey>>({ key: 'job', dir: 'asc' })
  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((left, right) => {
      const a = sortValue(left, sort.key)
      const b = sortValue(right, sort.key)
      if (a < b) return sort.dir === 'asc' ? -1 : 1
      if (a > b) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return copy
  }, [rows, sort])

  const onSort = (key: InspectionKey) => {
    setSort((current) => (
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ))
  }

  return (
    <DirectorySheet
      label="QA inspections"
      columns={COLUMNS}
      rows={sorted}
      rowKey={(row) => String(row.job_id)}
      sort={sort}
      onSort={onSort}
      onRowClick={(row) => openRow(row, pendingJobId, onOpenEmpty)}
      menuItems={(row) => rowMenu(row, pendingJobId, onOpenEmpty)}
      addLabel={onAdd ? 'New inspection' : undefined}
      onAdd={onAdd}
      renderCell={(column, row) => cell(column.key, row, pendingJobId, onOpenEmpty)}
    />
  )
}

function rowMenu(
  row: QaJobRow,
  pendingJobId: string | null,
  onOpenEmpty: (row: QaJobRow) => void,
): ContextMenuItem[] {
  return [
    { id: 'open', label: 'Open', onSelect: () => openRow(row, pendingJobId, onOpenEmpty) },
    {
      id: 'copy',
      label: 'Copy job number',
      onSelect: () => { void navigator.clipboard?.writeText(String(row.job_id)) },
    },
  ]
}

function cell(
  key: InspectionKey,
  row: QaJobRow,
  pendingJobId: string | null,
  onOpenEmpty: (row: QaJobRow) => void,
) {
  const latest = row.latest
  const status = String(latest?.status || '')
  const pending = pendingJobId === String(row.job_id)
  if (key === 'job') {
    if (latest) {
      return (
        <a
          href={qaRecordPath(latest.id)}
          aria-label={[`Job #${row.job_id}`, latest.inspection_name, row.client_name, row.site_address].filter(Boolean).join(', ')}
          onClick={(event) => event.stopPropagation()}
          className="font-medium tabular-nums text-foreground no-underline hover:underline"
        >
          Job #{row.job_id}
        </a>
      )
    }
    return (
      <button
        type="button"
        disabled={pending}
        onClick={(event) => {
          event.stopPropagation()
          onOpenEmpty(row)
        }}
        className="inline-flex items-center gap-2 text-left font-medium tabular-nums text-foreground hover:underline disabled:opacity-60"
      >
        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
        Job #{row.job_id}
      </button>
    )
  }
  if (key === 'client') return <span className="truncate">{row.client_name || '—'}</span>
  if (key === 'site') return <span className="truncate">{row.site_address || '—'}</span>
  if (key === 'inspection') return <span className="truncate">{latest?.inspection_name || '—'}</span>
  if (key === 'status') {
    return (
      <span className={`capitalize ${status ? statusTone(status) : 'text-muted-foreground'}`}>
        {status ? statusText(status) : '—'}
      </span>
    )
  }
  if (key === 'records') return row.qa_count
  return (
    <span className="text-muted-foreground">
      {latest?.created_at ? formatDate(latest.created_at) : '—'}
    </span>
  )
}
